import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PROGRAM_TYPES = [
  "MUN",
  "Debate",
  "Youth Parliament",
  "Leadership Bootcamp",
  "Educational Tour",
  "University Visit",
  "Custom Program",
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const AGE_GROUPS = [
  "Grade 6 – 8 (Middle School)",
  "Grade 9 – 10 (Secondary)",
  "Grade 11 – 12 (Senior Secondary)",
  "Undergraduate (Year 1 – 4)",
  "Postgraduate",
  "Mixed Cohort",
];

const initialState = {
  organization: "",      // Institution Name
  country: "",           // City / Country
  name: "",              // Coordinator Name
  role: "",              // Designation
  phone: "",             // Mobile Number
  email: "",
  approx_students: "",
  age_group: "",
  preferred_destination: "",
  program_type: "",
  preferred_month: "",
  message: "",
};

export default function DiplomexaLeadForm({
  testIdPrefix = "diplomexa-lead-form",
  title = "Plan a Student Delegation",
  subtitle = "Share your delegation details. Our team will respond within one business day.",
}) {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        inquiry_type: "delegation",
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you. Our delegation team will be in touch shortly.");
      setForm(initialState);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-200 text-navy placeholder:text-slate-400 px-4 py-3 text-sm focus:border-navy focus:outline-none focus:ring-0";
  const labelCls = "block text-xs uppercase tracking-wider mb-2 text-slate-600";

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-container`}
        className="bg-white border border-slate-200 p-8 sm:p-10"
      >
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500 mb-3">
          Inquiry Received
        </div>
        <h3 className="font-display text-2xl text-navy font-light tracking-tight">
          Thank you. We'll be in touch.
        </h3>
        <p className="mt-3 text-slate-600">
          A member of our delegation team will respond within one business day
          with a structured proposal for your cohort.
        </p>
        <button
          data-testid={`${testIdPrefix}-reset`}
          onClick={() => setDone(false)}
          className="mt-6 text-xs uppercase tracking-[0.18em] text-navy hover:text-gold-500"
        >
          Submit another inquiry →
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid={`${testIdPrefix}-container`}
      className="bg-white border border-slate-200 p-7 sm:p-10"
    >
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
          Diplomexa × Destineze · Delegation Inquiry
        </div>
        <h3 className="mt-2 font-display text-2xl sm:text-3xl text-navy font-light tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
      </div>

      <form
        onSubmit={submit}
        data-testid={`${testIdPrefix}-form`}
        className="space-y-5"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Institution Name *</label>
            <input
              required
              name="organization"
              value={form.organization}
              onChange={onChange}
              data-testid={`${testIdPrefix}-institution`}
              placeholder="Your school / college / university"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>City / Country *</label>
            <input
              required
              name="country"
              value={form.country}
              onChange={onChange}
              data-testid={`${testIdPrefix}-city-country`}
              placeholder="Dubai, UAE"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Coordinator Name *</label>
            <input
              required
              name="name"
              value={form.name}
              onChange={onChange}
              data-testid={`${testIdPrefix}-name`}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Designation *</label>
            <input
              required
              name="role"
              value={form.role}
              onChange={onChange}
              data-testid={`${testIdPrefix}-designation`}
              placeholder="Principal / Coordinator / Faculty"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Mobile Number *</label>
            <input
              required
              name="phone"
              value={form.phone}
              onChange={onChange}
              data-testid={`${testIdPrefix}-mobile`}
              placeholder="+971 50 …"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input
              required
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              data-testid={`${testIdPrefix}-email`}
              placeholder="you@institution.org"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Approx. Number of Students *</label>
            <input
              required
              type="number"
              min="1"
              name="approx_students"
              value={form.approx_students}
              onChange={onChange}
              data-testid={`${testIdPrefix}-students`}
              placeholder="e.g. 25"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Student Age Group / Grade *</label>
            <select
              required
              name="age_group"
              value={form.age_group}
              onChange={onChange}
              data-testid={`${testIdPrefix}-age-group`}
              className={inputCls}
            >
              <option value="">Select age group</option>
              {AGE_GROUPS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Preferred Destination</label>
            <input
              name="preferred_destination"
              value={form.preferred_destination}
              onChange={onChange}
              data-testid={`${testIdPrefix}-destination`}
              placeholder="Dubai, Singapore, Malaysia, UK …"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Interested Program Type *</label>
            <select
              required
              name="program_type"
              value={form.program_type}
              onChange={onChange}
              data-testid={`${testIdPrefix}-program-type`}
              className={inputCls}
            >
              <option value="">Select program type</option>
              {PROGRAM_TYPES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Preferred Month</label>
            <select
              name="preferred_month"
              value={form.preferred_month}
              onChange={onChange}
              data-testid={`${testIdPrefix}-month`}
              className={inputCls}
            >
              <option value="">Select month</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Message / Requirements *</label>
          <textarea
            required
            rows={4}
            name="message"
            value={form.message}
            onChange={onChange}
            data-testid={`${testIdPrefix}-message`}
            placeholder="Brief context — outcomes you'd like, any constraints, timeline."
            className={`${inputCls} resize-none`}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testIdPrefix}-submit`}
          className="group inline-flex items-center gap-3 bg-navy text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-navy-700 transition disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          )}
          Submit Delegation Inquiry
        </button>
        <p className="text-[11px] text-slate-500">
          By submitting, you agree to be contacted by Destineze regarding your
          delegation inquiry.
        </p>
      </form>
    </div>
  );
}
